import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import KatakanaTextField from 'components/form/KatakanaTextField';
import TextArea from 'components/form/TextArea';
import {
  MSG_INVALID_DATETIME_FORMAT,
  MSG_MAX_LENGTH,
  MSG_REQUIRED,
} from 'constant/ValidationMessages';
import { createClient } from 'stores/ClientStoreAdapter';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type ClientCreateFormHandle = {
  submit: () => void;
};

type ClientCreateFormProps = {
  clientTypes: MasterTypeResult[];
  onCreate: () => void;
  onCancel: () => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const ClientCreateForm = React.forwardRef<ClientCreateFormHandle, ClientCreateFormProps>(
  ({ clientTypes, onCreate, onCancel, setProgress, hideHeader = false }, ref) => {
    const [clientName, setClientName] = useState('');
    const [clientNameKana, setClientNameKana] = useState('');
    const [clientTypeId, setClientTypeId] = useState('');
    const [corporateNumber, setCorporateNumber] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [address, setAddress] = useState('');
    const [url, setUrl] = useState('');
    const [contactName, setContactName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [mailAddress, setMailAddress] = useState('');
    const [contractStartDate, setContractStartDate] = useState('');
    const [remarks, setRemarks] = useState('');

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
      // ラジオボタン初期値: 最初の選択肢
      if (clientTypes.length > 0) setClientTypeId(clientTypes[0].typeCode);
    }, [clientTypes]);

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!clientName) newErrors.clientName = MSG_REQUIRED('取引先名');
      else if (clientName.length > 300) newErrors.clientName = MSG_MAX_LENGTH('取引先名', 300);

      if (!clientNameKana) newErrors.clientNameKana = MSG_REQUIRED('取引先名（カナ）');
      else if (clientNameKana.length > 300)
        newErrors.clientNameKana = MSG_MAX_LENGTH('取引先名（カナ）', 300);

      if (!clientTypeId) newErrors.clientTypeId = MSG_REQUIRED('取引先区分');

      if (!contractStartDate) newErrors.contractStartDate = MSG_REQUIRED('取引開始日');
      else if (!DATETIME_REGEX.test(contractStartDate))
        newErrors.contractStartDate = MSG_INVALID_DATETIME_FORMAT('取引開始日');

      if (corporateNumber && corporateNumber.length > 20)
        newErrors.corporateNumber = MSG_MAX_LENGTH('法人番号', 20);

      if (invoiceNumber && invoiceNumber.length > 20)
        newErrors.invoiceNumber = MSG_MAX_LENGTH('インボイス番号', 20);

      if (address && address.length > 500) newErrors.address = MSG_MAX_LENGTH('住所', 500);

      if (url && url.length > 500) newErrors.url = MSG_MAX_LENGTH('企業ホームページ', 500);

      if (contactName && contactName.length > 300)
        newErrors.contactName = MSG_MAX_LENGTH('担当者名', 300);

      if (phoneNumber && phoneNumber.length > 20)
        newErrors.phoneNumber = MSG_MAX_LENGTH('電話番号', 20);

      if (mailAddress && mailAddress.length > 300)
        newErrors.mailAddress = MSG_MAX_LENGTH('メールアドレス', 300);

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleCreate = () => {
      if (!validate()) return;

      setProgress(true);
      createClient({
        clientName,
        clientNameKana,
        clientTypeId,
        ...(corporateNumber && { corporateNumber }),
        ...(invoiceNumber && { invoiceNumber }),
        ...(address && { address }),
        ...(url && { url }),
        ...(contactName && { contactName }),
        ...(phoneNumber && { phoneNumber }),
        ...(mailAddress && { mailAddress }),
        contractStartDate,
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('取引先を作成しました');
          onCreate();
        })
        .catch((e: Error) => {
          errorDefault('取引先の作成に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleCreate }));

    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader && (
          <Box sx={STICKY_HEADER_SX}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={onCancel}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" fontWeight={700}>
                  取引先登録
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
              新しい取引先を登録します
            </Typography>
          </Box>
        )}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="取引先名"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.clientName}
                  helperText={errors.clientName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="取引先名（カナ）"
                  value={clientNameKana}
                  onChange={(v) => setClientNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.clientNameKana}
                  helperText={errors.clientNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={12}>
                <FormControl error={!!errors.clientTypeId} required>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>取引先区分</FormLabel>
                  <RadioGroup
                    row
                    value={clientTypeId}
                    onChange={(e) => setClientTypeId(e.target.value)}
                  >
                    {clientTypes.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                  {errors.clientTypeId && <FormHelperText>{errors.clientTypeId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 取引期間 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              取引期間
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="start"
                  value={contractStartDate}
                  onChange={setContractStartDate}
                  label="取引開始日"
                  required
                  error={!!errors.contractStartDate}
                  helperText={errors.contractStartDate}
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value=""
                  onChange={() => {}}
                  label="取引終了日"
                  disabled
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 法人情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              法人情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="法人番号"
                  value={corporateNumber}
                  onChange={(e) => setCorporateNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.corporateNumber}
                  helperText={errors.corporateNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="インボイス番号"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.invoiceNumber}
                  helperText={errors.invoiceNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="住所"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.address}
                  helperText={errors.address}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="企業ホームページ"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.url}
                  helperText={errors.url}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 連絡先 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              連絡先
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="担当者名"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.contactName}
                  helperText={errors.contactName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="電話番号"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="メールアドレス"
                  value={mailAddress}
                  onChange={(e) => setMailAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.mailAddress}
                  helperText={errors.mailAddress}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

ClientCreateForm.displayName = 'ClientCreateForm';

export default ClientCreateForm;
